import { useMemo } from 'react'
import type { TShow } from '@schemas'

import { useDivisions } from '@/api/hooks'
import { useEnteredClasses } from '@/hooks/useEnteredClasses'
import { computeRunSectionStatuses, type RunSectionStatuses } from '@/lib/run-section-status'

export function useRunSectionStatuses(
  show: TShow | null | undefined,
  options?: { enabled?: boolean }
): {
  statuses: RunSectionStatuses | null
  isLoading: boolean
  isError: boolean
} {
  const showId = show?.id ?? undefined
  const enabled = (options?.enabled ?? true) && Boolean(showId)
  const queryShowId = enabled ? showId : undefined

  const divisionsQuery = useDivisions(queryShowId)
  const enteredClasses = useEnteredClasses(queryShowId)

  const statuses = useMemo(() => {
    if (!enabled || !show) return null
    // Compute with whatever data we have so awards/reports can reflect show.status immediately.
    return computeRunSectionStatuses({
      show,
      divisions: divisionsQuery.data ?? [],
      entered: enteredClasses.entered,
    })
  }, [enabled, show, divisionsQuery.data, enteredClasses.entered])

  const isLoading = enabled && (divisionsQuery.isLoading || enteredClasses.isLoading)
  const isError = Boolean(divisionsQuery.isError || enteredClasses.isError)

  return {
    statuses,
    isLoading,
    isError,
  }
}
